import { useEffect, useRef, useState } from "react";
import { FiKey, FiX, FiCopy, FiCheck } from "react-icons/fi";

type Cred = {
  label: string;
  value: string;
};

const CREDS: Cred[] = [
  { label: "Customer (Email)", value: "customer@example.com / Customer@123" },
  { label: "Customer (OTP)", value: "Phone: 9876543212" },
  { label: "Delivery Boy", value: "Phone + Password (Admin se create)" },
];

const TestCredentialsFab: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const btnRef = useRef<HTMLButtonElement | null>(null);

  // Close on outside click / Esc
  useEffect(() => {
    if (!open) return;

    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        panelRef.current && !panelRef.current.contains(target) &&
        btnRef.current && !btnRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleCopy = async (value: string, idx: number) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedIdx(idx);
      window.setTimeout(() => setCopiedIdx(null), 1500);
    } catch {
      // ignore
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Hide test credentials" : "Show test credentials"}
        title="Test Credentials"
        className="fixed bottom-6 right-6 z-50 flex items-center justify-center w-12 h-12 rounded-full bg-amber-500 hover:bg-amber-600 dark:bg-amber-500 dark:hover:bg-amber-400 text-white shadow-lg shadow-amber-500/30 transition-all hover:scale-105 active:scale-95"
      >
        {open ? <FiX size={20} /> : <FiKey size={18} />}
      </button>

      {/* Panel */}
      {open && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Test Credentials"
          className="fixed bottom-20 right-6 z-50 w-72 p-4 rounded-xl bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-500/30 shadow-2xl shadow-slate-900/10 dark:shadow-black/40"
        >
          <div className="flex items-center justify-between mb-3">
            <p className="text-[11px] font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider">
              Test Credentials
            </p>
            <span className="text-[10px] text-slate-400 dark:text-slate-500">Click to copy</span>
          </div>

          <div className="space-y-2">
            {CREDS.map((c, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleCopy(c.value, idx)}
                className="w-full text-left p-2.5 rounded-lg bg-amber-50 dark:bg-amber-500/10 hover:bg-amber-100 dark:hover:bg-amber-500/20 border border-amber-200/60 dark:border-amber-500/20 transition-colors group"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-medium text-amber-700 dark:text-amber-300 mb-0.5">
                      {c.label}
                    </p>
                    <p className="text-[11px] text-slate-600 dark:text-slate-300 break-all">
                      {c.value}
                    </p>
                  </div>
                  <span className="flex-shrink-0 mt-0.5 text-amber-500 dark:text-amber-400 opacity-60 group-hover:opacity-100 transition-opacity">
                    {copiedIdx === idx ? <FiCheck size={14} /> : <FiCopy size={14} />}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default TestCredentialsFab;
